import {
  Avatar,
  AvatarGroup,
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Spinner,
  Stack,
  Tooltip,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { GoPeople } from 'react-icons/go';
import { IoArrowUpCircleOutline } from 'react-icons/io5';
import { MdOutlinePeople, MdOutlinePerson } from 'react-icons/md';
import { RiCloseLine } from 'react-icons/ri';
import { RxPencil2 } from 'react-icons/rx';
import { TbWorld } from 'react-icons/tb';
import {
  ConversationDataFragment,
  ConversationsBasicDataFragment,
  ConversationType,
  DtType,
  MessageDataFragment,
  PerspectiveBaseInfoFragment,
  useGetAiMessageResponseMutation,
  useGetConversationLazyQuery,
  useSendAiMessageMutation,
  useProfileDropdownQuery,
  ProfileDropdownDocument,
  useMarkConversationViewedMutation,
} from '../../generated/graphql';
import ChatBubble from '../Chat/ChatBubble';
import { ChatIndicatorBubble } from '../Chat/ChatIndicatorBubble';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { DemographicAvatar } from './DemographicIcons';
import { ProjectCreateChat } from './ProjectCreateChat';
import { ModelCardData } from './ProjectView';
import { HelivanButton } from '../helivan/HelivanButton';

export const DEFAULT_AVATAR_COLOR = '#F9D5FF';

interface ProjectTabViewChatProps {
  perspectives: PerspectiveBaseInfoFragment[];
  topic: string;
  conversationId?: string;
  setConversationId: (id: string) => void;
  createNew: boolean;
  setCreateNew: (createNew: boolean) => void;
  allPersonas: { id: string; name: string }[];
  projectId: string;
  dtType: DtType;
  modelCardData?: ModelCardData;
  allChats: ConversationsBasicDataFragment[];
  setSelectedTwins: React.Dispatch<
    React.SetStateAction<Set<{ id: string; name: string }>>
  >;
  setDigitalTwinId: (id: string | undefined) => void;
  chatInitialType: ConversationType;
}

export const ProjectTabViewChat = ({
  perspectives,
  topic,
  conversationId,
  createNew,
  setCreateNew,
  allPersonas,
  setConversationId,
  projectId,
  dtType,
  modelCardData,
  allChats,
  setSelectedTwins,
  setDigitalTwinId,
  chatInitialType,
}: ProjectTabViewChatProps) => {
  const [activeMessage, setActiveMessage] = useState('');
  const [currentConversation, setCurrentConversation] =
    useState<ConversationDataFragment>();

  const getAvatarColor = (idx: number): string => {
    if (idx % 3 === 0) {
      return DEFAULT_AVATAR_COLOR;
    }
    if (idx % 3 === 1) {
      return '#E3F9B9';
    }
    return '#D9C3F6';
  };

  const [messages, setMessages] = useState<MessageDataFragment[]>([]);
  const [title, setTitle] = useState<string>('New Conversation');
  const [sendMessage, { loading: sendMessageLoading }] =
    useSendAiMessageMutation();
  const [getResponse, { loading: getResponseLoading }] =
    useGetAiMessageResponseMutation();
  const [getConversation, { loading: getConversationLoading }] =
    useGetConversationLazyQuery();
  const [markConversationViewed] = useMarkConversationViewedMutation();
  const { data: userData } = useProfileDropdownQuery();
  const canSendMessage = userData?.user?.canSendMessage ?? true;
  const [dtToColor, setDtToColor] = useState<{ dtId: string; color: string }[]>(
    []
  );

  const getDtColor = (dtId: string) => {
    return (
      dtToColor.find((dt) => dt.dtId === dtId)?.color ?? DEFAULT_AVATAR_COLOR
    );
  };

  const [chatFilter, setChatFilter] = useState<ConversationType>();

  useEffect(() => {
    if (
      currentConversation?.digitalTwins &&
      currentConversation.digitalTwins.length > 0
    ) {
      const colors: { dtId: string; color: string }[] = [];
      for (let i = 0; i < currentConversation.digitalTwins.length; i++) {
        colors.push({
          dtId: currentConversation.digitalTwins[i].id,
          color: getAvatarColor(i),
        });
      }
      setDtToColor(colors);
    }
  }, [currentConversation]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'; // Reset height before measuring
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Auto-resize
    }
  }, [activeMessage]);

  const messageContainerRef = useRef<HTMLDivElement>(null);

  // Add this useEffect to scroll to the bottom when messages change
  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [messages]); // This will trigger whenever messages are updated

  const handleSendMessage = () => {
    if (
      !currentConversation ||
      activeMessage.length === 0 ||
      sendMessageLoading
    )
      return;

    sendMessage({
      variables: {
        message: activeMessage,
        conversationId: currentConversation.id,
      },
      refetchQueries: [{ query: ProfileDropdownDocument }],
      onCompleted: (data) => {
        setActiveMessage('');
        if (data.sendAIMessage.messages) {
          setMessages(data.sendAIMessage.messages);
        }
        getResponse({
          variables: {
            conversationId: currentConversation.id,
          },
          onCompleted: (data) => {
            if (data.getAIMessageResponse.messages) {
              setMessages(data.getAIMessageResponse.messages);
            }
            if (data.getAIMessageResponse.title !== title) {
              setTitle(data.getAIMessageResponse.title);
            }
          },
        });
      },
    });
  };

  useEffect(() => {
    if (!conversationId) {
      setCurrentConversation(undefined);
    } else {
      if (conversationId !== currentConversation?.id) {
        getConversation({
          variables: {
            id: conversationId,
          },
          onCompleted: (data) => {
            setCurrentConversation(data.getConversation);
            if (data.getConversation.messages) {
              setMessages(data.getConversation.messages);
            }
            if (
              data.getConversation.digitalTwins.length === 1 &&
              data.getConversation.type === ConversationType.Persona
            ) {
              setDigitalTwinId(data.getConversation.digitalTwins[0].id);
              setSelectedTwins(
                new Set([
                  {
                    id: data.getConversation.digitalTwins[0].id,
                    name: data.getConversation.digitalTwins[0].name,
                  },
                ])
              );
            }
            // Mark conversation as viewed
            markConversationViewed({
              variables: {
                id: conversationId,
              },
            });
          },
        });
      }
    }
  }, [conversationId]);

  const filteredChats = (allChats ?? []).filter(
    (chat) => !chatFilter || chat.type === chatFilter
  );

  const demographics = currentConversation?.demographic?.split(', ') ?? [];

  return (
    <Flex width="full" height="full">
      {currentConversation || getConversationLoading || createNew ? (
        <Flex
          border="1px solid"
          borderColor={HelivanColor.border.primary}
          borderRadius="4px"
          width="full"
          height="full"
        >
          {createNew ? (
            <ProjectCreateChat
              setCreateNew={setCreateNew}
              allTwins={allPersonas}
              setConversationId={setConversationId}
              topic={topic}
              projectId={projectId}
              dtType={dtType}
              perspectives={perspectives}
              modelCardData={modelCardData}
              chatInitialType={chatInitialType}
            />
          ) : getConversationLoading ? (
            <Flex
              width="full"
              height="full"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Flex>
          ) : currentConversation ? (
            <Flex
              width="full"
              height="full"
              flexDirection="column"
              gap={6}
              p={4}
              justifyContent="space-between"
            >
              <Stack spacing={6} overflow="hidden">
                <Flex
                  width="full"
                  height="full"
                  justifyContent="space-between"
                  position="sticky"
                >
                  <Box
                    cursor="pointer"
                    p={1}
                    onClick={() => {
                      setCurrentConversation(undefined);
                    }}
                  >
                    <RiCloseLine size={24} color={HelivanColor.text.gray} />
                  </Box>
                  <Stack spacing={0.5} alignItems="center">
                    {currentConversation?.type === ConversationType.Persona ||
                    currentConversation?.type === ConversationType.Panel ? (
                      <AvatarGroup spacing="-8px">
                        {' '}
                        {(currentConversation?.digitalTwins ?? []).map(
                          (twin) => {
                            return (
                              <Avatar
                                key={twin.id}
                                size="sm"
                                name={twin.name}
                                bg={getDtColor(twin.id)}
                                color="black"
                              />
                            );
                          }
                        )}{' '}
                      </AvatarGroup>
                    ) : (
                      <AvatarGroup spacing="-8px">
                        {demographics.map((demographic, idx) => {
                          if (
                            demographic === 'Democrat' ||
                            demographic === 'Republican' ||
                            demographic === 'Independent'
                          ) {
                            return (
                              <DemographicAvatar
                                key={idx}
                                demographic={demographic}
                              />
                            );
                          }

                          return (
                            <Avatar
                              key={idx}
                              size="sm"
                              name={demographic}
                              bg={DEFAULT_AVATAR_COLOR}
                              color="black"
                            />
                          );
                        })}
                      </AvatarGroup>
                    )}

                    <HStack spacing={1}>
                      <HelivanText variant="largeBody" colorOverride="black">
                        {currentConversation.type ===
                        ConversationType.Demographic
                          ? currentConversation.demographic
                          : currentConversation.type === ConversationType.Panel
                            ? topic
                            : currentConversation.digitalTwins
                              ? currentConversation.digitalTwins[0].name
                              : undefined}
                      </HelivanText>
                      {title !== 'New Conversation' && (
                        <HelivanText variant="largeBody" colorOverride="black">
                          - {title}
                        </HelivanText>
                      )}
                    </HStack>
                  </Stack>
                  <Flex
                    cursor="pointer"
                    onClick={() => {
                      setCreateNew(true);
                    }}
                    borderRadius="4px"
                    alignItems="center"
                    justifyContent="center"
                    width="fit-content"
                    height="fit-content"
                    p={1}
                    _hover={{ bg: HelivanColor.hover.lightBlue }}
                  >
                    <RxPencil2 color={HelivanColor.text.blueDark} size={16} />
                  </Flex>
                </Flex>
                <Flex
                  width="full"
                  height="full"
                  pb={20}
                  flexDirection="column"
                  justifyContent="flex-end"
                >
                  <Stack
                    spacing={1}
                    overflowY="scroll"
                    ref={messageContainerRef}
                  >
                    {messages.map((msg, idx) => {
                      return (
                        <HStack spacing={3} width="full" key={idx}>
                          {!msg.userSender &&
                            (currentConversation.type ===
                              ConversationType.Demographic &&
                            demographics.length === 1 &&
                            (demographics.includes('Republican') ||
                              demographics.includes('Democrat') ||
                              demographics.includes('Independent')) ? (
                              <Flex
                                alignItems="flex-start"
                                height="full"
                                mt={2}
                              >
                                <DemographicAvatar
                                  demographic={demographics[0]}
                                />
                              </Flex>
                            ) : (
                              <Avatar
                                size="sm"
                                bg={
                                  msg.digitalTwin?.id
                                    ? getDtColor(msg.digitalTwin.id)
                                    : DEFAULT_AVATAR_COLOR
                                }
                                name={msg.digitalTwin?.name}
                                icon={
                                  !msg.digitalTwin?.name ? (
                                    <GoPeople size={16} />
                                  ) : undefined
                                }
                                color="black"
                                alignSelf="flex-start"
                                mt={
                                  currentConversation.type ===
                                  ConversationType.Panel
                                    ? 6
                                    : 2
                                }
                              />
                            ))}
                          <ChatBubble
                            content={msg.content}
                            senderUser={msg.userSender}
                            name={
                              currentConversation.type ===
                              ConversationType.Panel
                                ? msg.digitalTwin?.name
                                : undefined
                            }
                            retrievedContent={
                              msg.userSender
                                ? undefined
                                : msg.retrievedContent &&
                                    msg.retrievedContent.length > 0
                                  ? msg.retrievedContent
                                  : undefined
                            }
                          />
                        </HStack>
                      );
                    })}
                    {getResponseLoading && <ChatIndicatorBubble />}
                  </Stack>
                </Flex>
              </Stack>
              <Tooltip
                label="You're out of free credits. Contact info@helivan.io to pay for more. Credits update every week."
                isDisabled={canSendMessage}
                placement="top"
              >
                <InputGroup>
                  <Input
                    bg="white"
                    px={5}
                    py={4}
                    width="full"
                    borderRadius="12px"
                    height="50px"
                    borderWidth="1px"
                    value={activeMessage}
                    onChange={(e) => setActiveMessage(e.target.value)}
                    placeholder={`Ask ${currentConversation.type === ConversationType.Demographic ? currentConversation.demographic : 'me'} about ${topic}.`}
                    onKeyDown={(e) => {
                      // Send message on Enter key without Shift key
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault(); // Prevent default to avoid new line
                        handleSendMessage();
                      }
                    }}
                    disabled={sendMessageLoading || getResponseLoading || !canSendMessage}
                  />

                  <InputRightElement
                    height="full"
                    alignItems="center"
                    onClick={
                      sendMessageLoading || getResponseLoading || !canSendMessage
                        ? undefined
                        : () => {
                            handleSendMessage();
                          }
                    }
                    cursor={
                      sendMessageLoading || getResponseLoading || !canSendMessage
                        ? 'not-allowed'
                        : 'pointer'
                    }
                  >
                    <Box>
                      <IoArrowUpCircleOutline
                        size={30}
                        color={HelivanColor.text.blueDark}
                      />
                    </Box>
                  </InputRightElement>
                </InputGroup>
              </Tooltip>
            </Flex>
          ) : (
            <ProjectCreateChat
              setCreateNew={setCreateNew}
              allTwins={allPersonas}
              setConversationId={setConversationId}
              topic={topic}
              projectId={projectId}
              dtType={dtType}
              perspectives={perspectives}
              modelCardData={modelCardData}
              chatInitialType={chatInitialType}
            />
          )}
        </Flex>
      ) : (
        <Flex
          width="full"
          height="full"
          flexDirection="column"
          overflow="hidden"
          maxWidth="800px"
          mx="auto"
        >
          {/* Header with filters */}
          <Flex
            width="full"
            justifyContent="space-between"
            alignItems="center"
            mb={6}
          >
            <HelivanText
              variant="largeSubheader"
              colorOverride={HelivanColor.text.blueDark}
            >
              Recent Chats
            </HelivanText>
            <HStack spacing={3}>
              {/* Filter pills */}
              <HStack
                spacing={1}
                bg={HelivanColor.background.secondary}
                p={1}
                borderRadius="8px"
              >
                <Box
                  onClick={() => setChatFilter(undefined)}
                  px={3}
                  py={1.5}
                  borderRadius="6px"
                  bg={!chatFilter ? 'white' : 'transparent'}
                  boxShadow={!chatFilter ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ bg: !chatFilter ? 'white' : 'rgba(255,255,255,0.5)' }}
                >
                  <HelivanText
                    variant="smallBody"
                    colorOverride={!chatFilter ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                  >
                    All
                  </HelivanText>
                </Box>
                <Box
                  onClick={() => setChatFilter(ConversationType.Persona)}
                  px={3}
                  py={1.5}
                  borderRadius="6px"
                  bg={chatFilter === ConversationType.Persona ? 'white' : 'transparent'}
                  boxShadow={chatFilter === ConversationType.Persona ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ bg: chatFilter === ConversationType.Persona ? 'white' : 'rgba(255,255,255,0.5)' }}
                >
                  <HStack spacing={1}>
                    <MdOutlinePerson size={14} color={chatFilter === ConversationType.Persona ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid} />
                    <HelivanText
                      variant="smallBody"
                      colorOverride={chatFilter === ConversationType.Persona ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                    >
                      1-1
                    </HelivanText>
                  </HStack>
                </Box>
                <Box
                  onClick={() => setChatFilter(ConversationType.Panel)}
                  px={3}
                  py={1.5}
                  borderRadius="6px"
                  bg={chatFilter === ConversationType.Panel ? 'white' : 'transparent'}
                  boxShadow={chatFilter === ConversationType.Panel ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ bg: chatFilter === ConversationType.Panel ? 'white' : 'rgba(255,255,255,0.5)' }}
                >
                  <HStack spacing={1}>
                    <MdOutlinePeople size={14} color={chatFilter === ConversationType.Panel ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid} />
                    <HelivanText
                      variant="smallBody"
                      colorOverride={chatFilter === ConversationType.Panel ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                    >
                      Panel
                    </HelivanText>
                  </HStack>
                </Box>
                <Box
                  onClick={() => setChatFilter(ConversationType.Demographic)}
                  px={3}
                  py={1.5}
                  borderRadius="6px"
                  bg={chatFilter === ConversationType.Demographic ? 'white' : 'transparent'}
                  boxShadow={chatFilter === ConversationType.Demographic ? '0 1px 3px rgba(0,0,0,0.08)' : 'none'}
                  cursor="pointer"
                  transition="all 0.15s ease"
                  _hover={{ bg: chatFilter === ConversationType.Demographic ? 'white' : 'rgba(255,255,255,0.5)' }}
                >
                  <HStack spacing={1}>
                    <TbWorld size={14} color={chatFilter === ConversationType.Demographic ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid} />
                    <HelivanText
                      variant="smallBody"
                      colorOverride={chatFilter === ConversationType.Demographic ? HelivanColor.text.helivanBlue : HelivanColor.text.blueMid}
                    >
                      Crowd
                    </HelivanText>
                  </HStack>
                </Box>
              </HStack>
              {/* New chat button */}
              <HelivanButton
                size="sm"
                variant="primary"
                onClick={() => setCreateNew(true)}
              >
                <HStack spacing={1}>
                  <RxPencil2 size={14} />
                  <HelivanText variant="smallBody" colorOverride="white">
                    New Chat
                  </HelivanText>
                </HStack>
              </HelivanButton>
            </HStack>
          </Flex>

          {/* Chat list */}
          <Flex
            flex={1}
            bg="white"
            border="1px solid"
            borderColor={HelivanColor.border.primary}
            borderRadius="8px"
            overflow="hidden"
          >
            <Stack spacing={0} overflow="auto" width="full" height="full">
              {filteredChats.length > 0 ? (
                filteredChats.map((chat, idx) => (
                  <Flex
                    key={idx}
                    width="full"
                    p={4}
                    cursor="pointer"
                    onClick={() => setConversationId(chat.id)}
                    transition="all 0.15s ease"
                    borderBottom="1px solid"
                    borderBottomColor={HelivanColor.border.primary}
                    _last={{ borderBottom: 'none' }}
                    _hover={{
                      bg: HelivanColor.hover.lightBlue,
                    }}
                  >
                    <Flex width="full" justifyContent="space-between" alignItems="center">
                      <HStack spacing={3}>
                        <Flex
                          width="36px"
                          height="36px"
                          borderRadius="full"
                          bg={HelivanColor.background.secondary}
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon
                            as={
                              chat.type === ConversationType.Demographic
                                ? TbWorld
                                : chat.type === ConversationType.Panel
                                  ? MdOutlinePeople
                                  : MdOutlinePerson
                            }
                            boxSize={5}
                            color={HelivanColor.text.helivanBlue}
                          />
                        </Flex>
                        <Stack spacing={0}>
                          <HelivanText variant="largeBody+" colorOverride={HelivanColor.text.blueDark}>
                            {chat.type === ConversationType.Demographic
                              ? chat.demographic
                              : chat.digitalTwins?.map((twin) => twin.name).join(', ')}
                          </HelivanText>
                          <HelivanText variant="smallBody" colorOverride={HelivanColor.text.gray}>
                            {chat.type === ConversationType.Demographic
                              ? 'Crowd'
                              : chat.type === ConversationType.Panel
                                ? 'Panel'
                                : '1-1 Chat'}
                          </HelivanText>
                        </Stack>
                      </HStack>
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.gray}>
                        {chat.updatedAt}
                      </HelivanText>
                    </Flex>
                  </Flex>
                ))
              ) : (
                <Flex
                  width="full"
                  height="full"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  gap={4}
                  py={10}
                >
                  <HelivanText variant="largeBody" colorOverride={HelivanColor.text.gray}>
                    {chatFilter ? 'No chats found for this filter' : 'No recent chats yet'}
                  </HelivanText>
                  <HelivanButton
                    size="sm"
                    variant="primary"
                    onClick={() => setCreateNew(true)}
                  >
                    Start a new chat
                  </HelivanButton>
                </Flex>
              )}
            </Stack>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
